import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const currentDate = new Date().toLocaleDateString('it-IT', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-300">
      {/* Header */}
      <header className="border-b border-gray-800 bg-[#0a0a0a]/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link 
            to="/login" 
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Torna al Login</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-invert prose-gray max-w-none">
          <h1 className="text-3xl font-bold text-white mb-2">Informativa sulla Privacy</h1>
          <p className="text-gray-500 mb-8">Ultimo aggiornamento: {currentDate}</p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">1. Introduzione</h2>
            <p>
              Benvenuto in OBC Cloud. La presente Informativa sulla Privacy descrive come raccogliamo, 
              utilizziamo, elaboriamo e proteggiamo le informazioni personali degli utenti che utilizzano 
              la nostra applicazione e i nostri servizi.
            </p>
            <p>
              Utilizzando OBC Cloud, accetti le pratiche descritte in questa informativa. Ti invitiamo 
              a leggere attentamente questo documento per comprendere come gestiamo i tuoi dati personali.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">2. Titolare del Trattamento</h2>
            <p>
              Il Titolare del trattamento dei dati personali è OBC Cloud, raggiungibile all'indirizzo 
              email indicato nella sezione contatti dell'applicazione.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">3. Dati Raccolti</h2>
            <p>Raccogliamo le seguenti categorie di dati personali:</p>
            
            <h3 className="text-lg font-medium text-white mt-4 mb-2">3.1 Dati forniti dall'utente</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Dati di registrazione:</strong> nome, cognome, indirizzo email, password (criptata)</li>
              <li><strong>Dati di profilo:</strong> informazioni aggiuntive che scegli di fornire</li>
              <li><strong>Dati di comunicazione:</strong> messaggi inviati tramite il sistema di ticketing</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-4 mb-2">3.2 Dati raccolti automaticamente</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Dati di accesso:</strong> indirizzo IP, data e ora di accesso, tipo di browser</li>
              <li><strong>Dati del dispositivo:</strong> tipo di dispositivo, sistema operativo, identificatori univoci</li>
              <li><strong>Dati di utilizzo:</strong> pagine visitate, funzionalità utilizzate, interazioni con l'app</li>
              <li><strong>Dati biometrici:</strong> identificatori per l'autenticazione biometrica (memorizzati localmente sul dispositivo)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">4. Finalità del Trattamento</h2>
            <p>Utilizziamo i tuoi dati personali per:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Fornire, mantenere e migliorare i nostri servizi</li>
              <li>Creare e gestire il tuo account utente</li>
              <li>Elaborare transazioni e gestire abbonamenti</li>
              <li>Inviare comunicazioni relative al servizio (notifiche, aggiornamenti, avvisi di sicurezza)</li>
              <li>Fornire supporto tecnico e rispondere alle richieste</li>
              <li>Garantire la sicurezza del servizio e prevenire frodi</li>
              <li>Rispettare obblighi legali e normativi</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">5. Base Giuridica del Trattamento</h2>
            <p>Il trattamento dei tuoi dati personali si basa su:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Esecuzione del contratto:</strong> per fornirti i servizi richiesti</li>
              <li><strong>Consenso:</strong> per comunicazioni di marketing e funzionalità opzionali</li>
              <li><strong>Interesse legittimo:</strong> per migliorare i servizi e garantire la sicurezza</li>
              <li><strong>Obbligo legale:</strong> per rispettare normative applicabili</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">6. Condivisione dei Dati</h2>
            <p>
              Non vendiamo i tuoi dati personali a terzi. Possiamo condividere i tuoi dati solo nei 
              seguenti casi:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Fornitori di servizi:</strong> partner tecnici che ci aiutano a fornire il servizio 
                (hosting, elaborazione pagamenti, invio email)</li>
              <li><strong>Obblighi legali:</strong> quando richiesto dalla legge o da autorità competenti</li>
              <li><strong>Protezione dei diritti:</strong> per proteggere i nostri diritti, la privacy, 
                la sicurezza o la proprietà</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">7. Sicurezza dei Dati</h2>
            <p>
              Adottiamo misure di sicurezza tecniche e organizzative appropriate per proteggere i tuoi 
              dati personali, tra cui:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Crittografia dei dati in transito (HTTPS/TLS)</li>
              <li>Hashing delle password con algoritmi sicuri</li>
              <li>Autenticazione a due fattori (2FA/OTP)</li>
              <li>Autenticazione biometrica (opzionale)</li>
              <li>Accesso limitato ai dati basato su ruoli</li>
              <li>Monitoraggio e audit delle attività</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">8. Conservazione dei Dati</h2>
            <p>
              Conserviamo i tuoi dati personali per il tempo necessario a fornire i servizi richiesti 
              e per adempiere agli obblighi legali. In particolare:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Dati dell'account: fino alla cancellazione dell'account</li>
              <li>Dati di fatturazione: 10 anni (obblighi fiscali)</li>
              <li>Log di sicurezza: 12 mesi</li>
              <li>Dati di supporto: 3 anni dalla chiusura del ticket</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">9. I Tuoi Diritti</h2>
            <p>In conformità con il GDPR, hai diritto a:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Accesso:</strong> richiedere una copia dei tuoi dati personali</li>
              <li><strong>Rettifica:</strong> correggere dati inesatti o incompleti</li>
              <li><strong>Cancellazione:</strong> richiedere la cancellazione dei tuoi dati ("diritto all'oblio")</li>
              <li><strong>Limitazione:</strong> limitare il trattamento dei tuoi dati</li>
              <li><strong>Portabilità:</strong> ricevere i tuoi dati in formato strutturato</li>
              <li><strong>Opposizione:</strong> opporti al trattamento per motivi legittimi</li>
              <li><strong>Revoca del consenso:</strong> ritirare il consenso in qualsiasi momento</li>
            </ul>
            <p className="mt-4">
              Per esercitare questi diritti, contattaci tramite l'indirizzo email indicato nella sezione 
              contatti dell'applicazione.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">10. Cookie e Tecnologie Simili</h2>
            <p>
              Utilizziamo cookie tecnici necessari per il funzionamento del servizio. Non utilizziamo 
              cookie di profilazione per scopi pubblicitari.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Cookie di sessione:</strong> per mantenere l'autenticazione</li>
              <li><strong>Cookie di preferenze:</strong> per memorizzare le tue preferenze</li>
              <li><strong>Local Storage:</strong> per memorizzare dati dell'applicazione</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">11. Trasferimento Internazionale</h2>
            <p>
              I tuoi dati potrebbero essere trasferiti e conservati su server situati al di fuori del 
              tuo paese di residenza. In tal caso, adottiamo misure appropriate per garantire che i 
              tuoi dati siano protetti in conformità con questa informativa e le leggi applicabili.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">12. Minori</h2>
            <p>
              I nostri servizi non sono destinati a minori di 16 anni. Non raccogliamo consapevolmente 
              dati personali di minori. Se veniamo a conoscenza di aver raccolto dati di un minore, 
              provvederemo a eliminarli tempestivamente.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">13. Modifiche all'Informativa</h2>
            <p>
              Potremmo aggiornare questa Informativa sulla Privacy periodicamente. Ti informeremo di 
              eventuali modifiche significative tramite email o notifica nell'applicazione. Ti invitiamo 
              a consultare regolarmente questa pagina per rimanere aggiornato.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">14. Contatti</h2>
            <p>
              Per domande, richieste o reclami relativi a questa Informativa sulla Privacy o al 
              trattamento dei tuoi dati personali, puoi contattarci:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Tramite il sistema di ticketing dell'applicazione</li>
              <li>Tramite l'indirizzo email indicato nelle impostazioni dell'app</li>
            </ul>
            <p className="mt-4">
              Hai inoltre il diritto di presentare un reclamo all'Autorità Garante per la Protezione 
              dei Dati Personali se ritieni che il trattamento dei tuoi dati violi le normative applicabili.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-500">
          <p>&copy; {new Date().getFullYear()} OBC Cloud. Tutti i diritti riservati.</p>
        </div>
      </footer>
    </div>
  );
}
